import * as TFF from '@tff/types/tff';
import { IntlShape } from 'react-intl';

/**
 * Returns the formatted baggage text for HAND_BAGGAGE and BAGGAGE ancillary types.
 * Returns undefined for other ancillary types.
 */
export const getNdcBaggageText = (ancillary: TFF.Ancillary, intl: IntlShape): string | undefined => {
  const pieces = ancillary.AdditionalDetails?.Pieces || 1;
  const baggageSize = ancillary.Value?.toLowerCase() ?? '';

  switch (ancillary.Type) {
    case 'HAND_BAGGAGE':
      return intl.formatMessage({ id: 'ancillary.ndc.handLuggage' }, { pieces, baggageSize });
    case 'BAGGAGE':
      return intl.formatMessage({ id: 'ancillary.ndc.baggage' }, { pieces, baggageSize });
    default:
      return undefined;
  }
};

/**
 * Gets journey-specific ancillaries plus truly default ones for NDC bookings.
 *
 * Combines ancillaries from the specific journey's FareProduct with truly default
 * ancillaries that exist at passenger level but don't appear in any FareProduct.
 * This prevents duplicates while ensuring default ancillaries are shown on all journeys.
 * Only returns ancillaries of type BAGGAGE or HAND_BAGGAGE.
 *
 * @param passenger - The passenger object
 * @param journey - The current journey
 * @param orderDetails - Order details containing ancillary fares
 * @param ancillaries - All available ancillaries
 * @returns Array of baggage-related ancillaries for the passenger and journey
 */
export const getNdcPassengerAncillaries = (
  passenger: TFF.Passenger,
  journey: TFF.Journey,
  orderDetails: TFF.OrderDetails,
  ancillaries: TFF.Ancillaries,
): TFF.Ancillary[] => {
  const passengerFare = orderDetails.Fares?.[passenger.Id];
  if (!passengerFare) return [];

  const journeyAncillaryIds = passengerFare.FareProducts?.find(fp => fp.JourneyOnd === journey.Ond)?.AncillaryIDs ?? [];
  const allPassengerAncillaryIds = passengerFare.AncillaryIDs ?? [];
  const allJourneyAncillaryIds = passengerFare.FareProducts?.flatMap(fp => fp.AncillaryIDs) ?? [];
  const defaultAncillaryIds = allPassengerAncillaryIds.filter(id => !allJourneyAncillaryIds.includes(id));
  const combinedIds = [...journeyAncillaryIds, ...defaultAncillaryIds];

  return combinedIds
    .map((ancillaryId: string) => ancillaries[ancillaryId])
    .filter(Boolean)
    .filter(ancillary => ancillary.Type === 'BAGGAGE' || ancillary.Type === 'HAND_BAGGAGE');
};
